import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { v4 as isUUID } from "is-uuid";
import type { SortingState } from "@tanstack/react-table";
import type { FilterRow, FilterValueItem } from "../../components/shared/filters/Filters/types";

export const filterRegex = /filter\[([^[\]]*)\]\[([^[\]]*)\]/;
export const sortRegex = /sortBy\[([^\[\]]*)\]/;

const parseJSON = (value: string) => {
    if (!value.startsWith("{")) return value;
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

export const parseFiltersFromSearchParams = (searchParams: URLSearchParams) => {
    const filters: Record<string, FilterRow> = {};
    const sortBy: SortingState = [];
    const result: Record<string, unknown> = {};

    for (const [name, value] of searchParams.entries()) {
        const filterMatch = filterRegex.exec(name);

        if (filterMatch) {
            const [, field, method] = filterMatch;
            const isMulti = name.endsWith("[]");

            if (!filters[field]) {
                filters[field] = {
                    name: field,
                    method,
                    value: isMulti ? [] : undefined,
                };
            }

            if (!value) continue;

            const parsedValue: FilterValueItem = isUUID(value)
                ? { id: value }
                : value;

            if (isMulti) {
                (filters[field].value as FilterValueItem[]).push(
                    parsedValue
                );
            } else {
                filters[field].value = parsedValue;
            }

            continue;
        }

        const sortMatch = sortRegex.exec(name);

        if (sortMatch) {
            sortBy.push({
                id: sortMatch[1],
                desc: value === "desc",
            });

            continue;
        }

        result[name] = isUUID(value) ? { id: value } : parseJSON(value);
    }

    return {
        ...result,
        filters: Object.values(filters),
        sortBy,
    } as any;
};

const applyEntry = (prev: URLSearchParams, next: URLSearchParams, key: string, values: any) => {
    if (key === "filters") {
        for (const [entryKey] of prev.entries()) {
            if (filterRegex.test(entryKey)) next.delete(entryKey);
        }

        (values || [] as FilterRow[]).forEach(({ name, method, value }: FilterRow) => {
            const filterName = `filter[${name}][${method || ""}]`;

            if (Array.isArray(value)) {
                if (value.length > 0) {
                    value.forEach((item) => {
                        next.append(`${filterName}[]`, typeof item === "object" ? item.id : item);
                    });
                }
            } else if (value !== undefined && value !== null && value !== "") {
                next.set(filterName, typeof value === "object" ? value.id : value);
            }
        });

        return;
    }

    if (key === "sortBy") {
        for (const [entryKey] of prev.entries()) {
            if (sortRegex.test(entryKey)) next.delete(entryKey);
        }

        const current = parseFiltersFromSearchParams(prev).sortBy as SortingState;
        const resolved = typeof values === "function" ? values(current) : values;

        ((resolved || []) as SortingState).forEach(({ id, desc }) => {
            next.set(`sortBy[${id}]`, desc ? "desc" : "asc");
        });

        return;
    }

    if (values === undefined || values === null || values === "") {
        next.delete(key);
    } else {
        next.set(key, typeof values === "object" ? values.id : values);
    }
};

export const useFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const filters = useMemo(() => parseFiltersFromSearchParams(searchParams), [searchParams]);

    const setFilters = useCallback((keyOrEntries: string | Record<string, any>, values?: any, options?: { replace?: boolean }) => {
        const entries: Record<string, any> = typeof keyOrEntries === "string" ? { [keyOrEntries]: values } : keyOrEntries;

        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);

            for (const [key, values] of Object.entries(entries)) {
                applyEntry(prev, next, key, values);
            }

            if (!("page" in entries)) {
                next.set("page", "1");
            }

            return next;
        }, { replace: options?.replace ?? true });
    }, [setSearchParams]);

    return [filters, setFilters] as const;
};
